"use client";

import { useQueryClient } from "@tanstack/react-query";
import { useTranslations } from "next-intl";
import type React from "react";
import { useState } from "react";
import { useDisconnect } from "wagmi";
import { getProfileLinksData } from "@/components/features/profile";
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui";
import { useAuth } from "@/context/auth";
import { Link, useRouter } from "@/i18n/navigation";
import { cn } from "@/lib/utils";

interface SideNavAccountLinksProps {
  role?: "USER" | "ADMIN" | "GUEST";
  currentPath: string;
}

const isRowActive = (currentPath: string, href: string): boolean =>
  currentPath === href || currentPath.startsWith(`${href}/`);

/**
 * The Account submenu, rendered as lightweight rail-native rows. It reuses the
 * profile menu MODEL (`getProfileLinksData`) but NOT the full-page `LinkItem`
 * presentation (p-6 boxes + per-row borders + chevrons), so the rail stays
 * compact while both surfaces share one source of truth. A single hairline
 * guide on the inline-start gives the nested list structure without boxes.
 */
export const SideNavAccountLinks: React.FC<SideNavAccountLinksProps> = ({
  role,
  currentPath,
}) => {
  const t = useTranslations("profile");
  const router = useRouter();
  const { logout } = useAuth();
  const { disconnect } = useDisconnect();
  const queryClient = useQueryClient();
  const [logoutOpen, setLogoutOpen] = useState(false);

  const handleLogoutConfirm = async () => {
    await logout();
    disconnect();
    queryClient.clear();
    setLogoutOpen(false);
    router.push("/");
  };

  const links = getProfileLinksData(t, role, false, () => setLogoutOpen(true));

  const rowBase =
    "block w-full rounded-lg px-3 py-1.5 text-[13px] leading-6 transition-colors";

  return (
    <>
      <div className="mt-1 ms-3.5 flex flex-col gap-0.5 border-s border-stroke-subtle/60 ps-3">
        {links.map((link) => {
          if (link.disabled) {
            return (
              <span
                key={link.href}
                className={cn(rowBase, "cursor-default text-text-disabled")}
              >
                {link.label}
              </span>
            );
          }

          const active = isRowActive(currentPath, link.href);
          const interactive = cn(
            rowBase,
            active
              ? "text-accent"
              : "text-text-secondary hover:bg-bg-secondary/50 hover:text-text-primary",
          );

          if (link.onClick) {
            return (
              <button
                key={link.href}
                type="button"
                onClick={link.onClick}
                className={cn(interactive, "text-start")}
              >
                {link.label}
              </button>
            );
          }

          return (
            <Link
              key={link.href}
              href={link.href}
              aria-current={active ? "page" : undefined}
              className={interactive}
            >
              {link.label}
            </Link>
          );
        })}
      </div>

      <Dialog open={logoutOpen} onOpenChange={setLogoutOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-center text-sm">
              {t("links.logoutDialogTitle")}
            </DialogTitle>
            <DialogDescription className="text-center text-text-secondary">
              {t("links.logoutDialogDescription")}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <div className="flex gap-2 sm:gap-2">
              <Button
                variant="bordered"
                onClick={() => setLogoutOpen(false)}
                className="flex-1"
              >
                {t("links.logoutDialogCancel")}
              </Button>
              <Button
                variant="solid"
                onClick={handleLogoutConfirm}
                colors="primary"
                className="flex-1"
              >
                {t("links.logoutDialogConfirm")}
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
